import React from "react"
import styled, { css } from "styled-components"
import PropTypes from "prop-types"

import PlayIconSVG from "../images/icons/play.inline.svg"
import ArrowRightSVG from "../images/icons/arrow-right.inline.svg"
import SpinnerSVG from "../images/icons/spinner.inline.svg"

const ButtonComponent = styled.div`
  align-items: center;
  appearance: none;
  backface-visibility: hidden;
  background-color: var(--color-spaceblue);
  border: 2px solid var(--color-spaceblue);
  border-radius: 30px;
  color: var(--color-white);
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: var(--font-size-18);
  font-weight: 400;
  flex: none;
  line-height: 1;
  overflow: hidden;
  padding: 5px 30px;
  min-height: 58px;
  position: relative;
  text-align: center;
  text-decoration: none;
  transition:
    background-color var(--transitionTime),
    border-color var(--transitionTime),
    color var(--transitionTime),
    opacity var(--transitionTime);
  white-space: nowrap;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  > * {
    z-index: 1;
  }

  svg {
    flex: none;
    margin-left: 10px;
  }

  > .play {
    margin: 0 12px 0 -18px;
    display: block;
    height: 36px;
    width: 36px;

    svg {
      height: 100%;
      width: auto;
      margin: 0;
    }
  }

  > .spinner {
    margin-right: -6px;
    display: block;
  }

  &:hover {
    background-color: var(--color-sun);
    border-color: var(--color-sun);
    color: var(--color-white);
    text-decoration: none;
  }

  ${(props) =>
    props.$small &&
    css`
      font-size: var(--font-size-16);
      min-height: 40px;
      padding: 5px 10px;

      svg {
        margin-left: 5px;
      }
    `}

  ${(props) =>
    props.$highlight &&
    css`
      background-color: var(--color-sun);
      border-color: var(--color-sun);

      &:hover {
        background-color: var(--color-spaceblue);
        border-color: var(--color-spaceblue);
      }
    `};

  ${(props) =>
    props.$outline &&
    css`
      color: var(--color-spaceblue);
      background-color: transparent;
      border-color: var(--color-spaceblue);

      &:hover {
        background-color: var(--color-spaceblue);
        border-color: var(--color-spaceblue);
        color: var(--color-white);
      }
    `};

  ${(props) =>
    props.$outline &&
    props.$highlight &&
    css`
      color: var(--color-sun);
      background-color: transparent;
      border-color: var(--color-sun);

      &:hover {
        background-color: var(--color-sun);
        border-color: var(--color-sun);
        color: var(--color-white);
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `};

  ${(props) =>
    props.disabled &&
    props.$highlight &&
    css`
      background-color: var(--color-greyWarm);
      border-color: var(--color-greyWarm);
      opacity: 1;
    `};

  ${(props) =>
    props.disabled &&
    props.$outline &&
    props.$highlight &&
    css`
      background-color: transparent;
      border-color: var(--color-greyWarm);
      color: var(--color-greyWarm);
      opacity: 1;
    `};
`

export const TextButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
`

const Button = ({
  arrow,
  as,
  children,
  className,
  highlight,
  outline,
  play,
  small,
  spinner,
  ...props
}) => {
  return (
    <ButtonComponent
      className={`${className || ""} button`}
      $outline={outline}
      $highlight={highlight}
      $small={small}
      as={as}
      {...props}
    >
      <>
        {play && (
          <span className="play">
            <PlayIconSVG />
          </span>
        )}
        <span>{children}</span>
        {arrow && <ArrowRightSVG />}
        {spinner && (
          <span className="spinner">
            <SpinnerSVG />
          </span>
        )}
      </>
    </ButtonComponent>
  )
}

Button.propTypes = {
  arrow: PropTypes.bool,
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.func])
    .isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
  highlight: PropTypes.bool,
  outline: PropTypes.bool,
  small: PropTypes.bool,
  spinner: PropTypes.bool,
}

export default Button
